import { useState, useEffect, useRef, useCallback, type ElementType } from 'react';
import {
  Tray, PaperPlaneTilt, NotePencil, Archive as ArchiveIcon,
  Flag, Hourglass, Money, Paperclip, Scales, Sparkle, LinkBreak,
  MagnifyingGlass, ArrowSquareOut, PencilSimple, EnvelopeSimple, EnvelopeOpen,
  Star, Trash, WarningCircle, Rows, Clock,
  HouseSimple, CalendarBlank, Briefcase, ListChecks, ChatTeardropDots,
  CaretRight, CaretLeft, List,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchMailboxes, fetchFolderCounts, beginOAuthConnect, fetchMailboxHealth, oauthProviderForMailbox,
  type ConnectedMailbox, type FolderCounts, type FolderCountEntry, type MailboxHealth,
} from '../../lib/mailboxApi';
import {
  fetchEmails, fetchConversations, fetchSyncStatus, archiveEmail, toggleFollowUp,
  linkEmailToMatter, linkConversationToMatter, markRead, toggleStar, toggleConversationStar,
  dismissConversationSuggestion, bulkArchive, bulkMarkRead, bulkDelete,
  searchEmails, unlinkEmailFromMatter, type EmailSearchResult,
  type InboxFolder, type EmailSummary, type ConversationSummary, type SyncJob,
} from '../../lib/inboxApi';
import { getApiErrorMessage } from '@/lib/api';
import { ComposeModal } from '@/components/inbox/ComposeModal';
import { LogEnquiryModal } from '@/components/shared/LogEnquiryModal';
import { InboxEmailRow } from '@/components/inbox/InboxEmailRow';
import { InboxConversationRow } from '@/components/inbox/InboxConversationRow';
import { InboxBulkBar } from '@/components/inbox/InboxBulkBar';
import { InboxThreadPane } from '@/components/inbox/InboxThreadPane';
import { InboxConversationPane } from '@/components/inbox/InboxConversationPane';
import { ConversationIntelligencePanel } from '@/components/inbox/ConversationIntelligencePanel';
import { CommunicationContextPanel } from '@/components/inbox/CommunicationContextPanel';
import { InboxKeyboardHandler } from '@/components/inbox/InboxKeyboardHandler';
import { useMattersStore } from '@/lib/mattersStore';
import { fetchLabels } from '@/lib/inboxLabelsApi';
import { onInboxUpdate } from '@/lib/websocket';
import { CommunicationsTabBar } from '@/components/shared/CommunicationsTabBar';
import { OAuthReturnNotice } from '@/components/shared/OAuthReturnNotice';

// ─── Panel width constants ─────────────────────────────────────

const FOLDER_WIDTH_DEFAULT = 180;
const FOLDER_WIDTH_MIN = 0;
const FOLDER_WIDTH_MAX = 240;
const LIST_WIDTH_DEFAULT = 300;
const LIST_WIDTH_MIN = 240;
const LIST_WIDTH_MAX = 420;

function initWidth(key: string, fallback: number): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return parseInt(stored, 10);
  } catch { /* ignore */ }
  return fallback;
}

// ─── Folder definitions ────────────────────────────────────────

interface FolderDef {
  id: InboxFolder;
  label: string;
  Icon: ElementType;
  countKey: keyof FolderCounts | null;
  showUnread?: boolean;
  separator?: boolean;
}

// Legal-first order: primary → legal workspace → standard mail
const FOLDERS: FolderDef[] = [
  // Primary
  { id: 'inbox',             label: 'Inbox',             Icon: Tray,          countKey: 'inbox',             showUnread: true },
  { id: 'starred',           label: 'Starred',           Icon: Star,          countKey: 'starred' },
  { id: 'drafts',            label: 'Drafts',            Icon: NotePencil,    countKey: 'drafts' },
  { id: 'scheduled',         label: 'Scheduled',         Icon: Clock,         countKey: 'scheduled' },
  // Legal workspace — matter-centric, high-priority work
  { id: 'follow_ups',            label: 'Follow Up',         Icon: Flag,      countKey: 'followUps',            separator: true },
  { id: 'awaiting_response',     label: 'Awaiting Reply',    Icon: Hourglass, countKey: 'awaitingResponse' },
  { id: 'matter_linked',         label: 'Matter Linked',     Icon: Scales,    countKey: 'matterLinked' },
  { id: 'suggested',             label: 'Suggested Links',   Icon: Sparkle,   countKey: 'suggested' },
  { id: 'unlinked',              label: 'Unlinked',          Icon: LinkBreak, countKey: 'unlinked' },
  // Standard mail
  { id: 'all_mail',              label: 'All Mail',          Icon: Rows,          countKey: 'allMail',   showUnread: true, separator: true },
  { id: 'sent',                  label: 'Sent',              Icon: PaperPlaneTilt, countKey: 'sent' },
  { id: 'billing_opportunities', label: 'Billing',           Icon: Money,         countKey: 'billingOpportunities' },
  { id: 'attachments',           label: 'Attachments',       Icon: Paperclip,     countKey: 'attachments' },
  { id: 'archive',               label: 'Archive',           Icon: ArchiveIcon,   countKey: 'archive' },
  { id: 'spam',                  label: 'Spam',              Icon: WarningCircle, countKey: 'spam' },
  { id: 'trash',                 label: 'Trash',             Icon: Trash,         countKey: 'trash' },
];

// Intended folder grouping — ALL folders keep their place here (nothing deleted).
const FOLDER_SECTIONS: { label: string; ids: InboxFolder[] }[] = [
  { label: 'Communications', ids: ['inbox', 'awaiting_response', 'follow_ups'] },
  { label: 'Legal Workflow', ids: ['matter_linked', 'suggested', 'unlinked'] },
  { label: 'Email',          ids: ['drafts', 'scheduled', 'sent', 'archive', 'attachments'] },
];
const MORE_FOLDER_IDS: InboxFolder[] = ['all_mail', 'starred', 'billing_opportunities', 'trash', 'spam'];

// TRUTH-FIRST: a folder stays hidden until its workflow is actually implemented,
// so the UI never promises behaviour the backend can't deliver.
//   →  TO RESTORE A FOLDER: delete its line from this set. It reappears in its
//      section above. Its definition, counts and filters were never removed.
// See project_email_folder_audit.
const HIDDEN_FOLDERS = new Set<InboxFolder>([
  // 'awaiting_response' RESTORED 2026-06-25 (#5) — now engine-driven: a thread is
  // awaiting when its latest message is outbound with no inbound since (derived
  // from lastOutboundAt/lastInboundAt, never a manual flag).
  'follow_ups',            // restore once follow-up dates/reminders exist
  // 'suggested' RESTORED 2026-06-24 — classifier output now propagates to the
  // conversation list (conversation-sync best-link + post-classify recalc).
  // 'starred' RESTORED 2026-06-25 (#4) — star now mirrors to Gmail/Outlook with
  // durable retry and the sync no longer reverts a locally-set star.
  'billing_opportunities', // restore once a real billing classifier exists
]);
const FOLDER_BY_ID: Record<string, FolderDef> = Object.fromEntries(FOLDERS.map((f) => [f.id, f]));

function getFolderDisplayCount(folder: FolderDef, counts: FolderCounts): number | null {
  if (!folder.countKey) return null;
  const entry: FolderCountEntry | undefined = counts[folder.countKey];
  if (!entry) return null;
  const value = folder.showUnread ? entry.unread : entry.total;
  return value > 0 ? value : null;
}

// ─── Helpers ──────────────────────────────────────────────────

function providerLabel(provider: ConnectedMailbox['provider']): string {
  switch (provider) {
    case 'gmail':             return 'Gmail';
    case 'google_workspace':  return 'Google Workspace';
    case 'microsoft_365':     return 'Microsoft 365';
    case 'exchange':          return 'Exchange';
    case 'imap':              return 'IMAP';
  }
}

function providerColor(provider: ConnectedMailbox['provider']): string {
  switch (provider) {
    case 'gmail':
    case 'google_workspace': return '#EA4335';
    case 'microsoft_365':
    case 'exchange':         return '#0078D4';
    default:                  return '#6b7280';
  }
}

// ─── Connect mailbox panel ─────────────────────────────────────

function ConnectMailboxPanel({ onConnect }: { onConnect: (provider: 'google' | 'microsoft') => void }) {
  return (
    <div
      className="gap-6 p-6 sm:gap-8 sm:p-12"
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12, background: '#1e3a5f',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <EnvelopeSimple size={26} color="white" />
        </div>
        <h2 style={{
          fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 20,
          fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8,
        }}>
          Connect your mailbox
        </h2>
        <p style={{
          fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14,
          color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 28,
        }}>
          Link your Gmail or Microsoft 365 account to manage all correspondence
          from within Jurivoxa. Emails are automatically matched to matters.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            onClick={() => onConnect('google')}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
              border: '1px solid var(--border-strong)', borderRadius: 8,
              color: 'var(--text-primary)', background: 'var(--surface-card)', fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 14, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M5.266 9.765C6.199 6.939 8.854 4.91 12 4.91c1.654 0 3.146.575 4.312 1.522l3.38-3.38C17.634 1.164 15.013 0 12 0 7.27 0 3.198 2.671 1.24 6.65l4.026 3.115z"/>
              <path fill="#34A853" d="M16.041 18.013C14.951 18.716 13.566 19.09 12 19.09c-3.133 0-5.78-2.013-6.723-4.822L1.237 17.335C3.193 21.294 7.265 24 12 24c2.933 0 5.735-1.043 7.834-3.001l-3.793-2.986z"/>
              <path fill="#4A90E2" d="M19.834 21C22.029 18.952 23.455 15.904 23.455 12c0-.709-.091-1.418-.273-2.09H12v4.545h6.436c-.318 1.56-1.172 2.818-2.395 3.559L19.834 21z"/>
              <path fill="#FBBC05" d="M5.277 14.268A7.12 7.12 0 014.91 12c0-.782.135-1.533.357-2.235L1.24 6.65A11.94 11.94 0 000 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067z"/>
            </svg>
            Connect Gmail / Google Workspace
          </button>

          <button
            onClick={() => onConnect('microsoft')}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
              border: '1px solid var(--border-strong)', borderRadius: 8,
              color: 'var(--text-primary)', background: 'var(--surface-card)', fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 14, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 23 23">
              <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
              <path fill="#f35325" d="M1 1h10v10H1z"/>
              <path fill="#81bc06" d="M12 1h10v10H12z"/>
              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
              <path fill="#ffba08" d="M12 12h10v10H12z"/>
            </svg>
            Connect Microsoft 365
          </button>
        </div>

        <p style={{
          fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
          // --text-subtle measured 2.79:1 on this card at 12px. --text-muted is the token
          // intended for readable secondary copy and clears AA in both themes. A sentence about
          // how credentials are protected is precisely the one a user must be able to read.
          color: 'var(--text-muted)',
          marginTop: 20,
        }}>
          Credentials are encrypted end-to-end. Jurivoxa cannot read your password.
        </p>
      </div>
    </div>
  );
}

// ─── Main InboxPage ────────────────────────────────────────────

export default function InboxPage({ standalone = false }: { standalone?: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [activeFolder, setActiveFolder] = useState<InboxFolder>('inbox');
  const [activeMailboxId, setActiveMailboxId] = useState<string | null>(null);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false); // collapsed "More" group in the workspace sidebar
  const [matterLinkEmailId, setMatterLinkEmailId] = useState<string | null>(null);
  // Whether the matter-link target is a conversation (whole thread) vs a single email.
  const [matterLinkIsConversation, setMatterLinkIsConversation] = useState(false);
  // Set when a human classifies a conversation as new work; drives the enquiry form.
  const [enquiryConversationId, setEnquiryConversationId] = useState<string | null>(null);
  const [selectedEmailMatter, setSelectedEmailMatter] = useState<{ id: string; caseNumber: string; caseName: string } | null>(null);
  const [matterLinkSearch, setMatterLinkSearch] = useState('');
  const [matterLinkSelected, setMatterLinkSelected] = useState('');

  // ─── Workspace layout state ──────────────────────────────────
  const [folderWidth, setFolderWidth] = useState(() => initWidth('comm-folder-width', FOLDER_WIDTH_DEFAULT));
  const [listWidth, setListWidth] = useState(() => initWidth('comm-list-width', LIST_WIDTH_DEFAULT));

  // Responsive zones. Four fixed zones (52 rail + 180 folders + 300 list + reading) need
  // ~900px before the reading pane has any room, so below that they are shed in order of
  // what a reader can do without: the intelligence panel, then the folder sidebar and rail,
  // and finally the list itself, which steps aside once an email is open.
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isPhone = viewportWidth < 768;
  const isCompact = viewportWidth < 1280;
  const [foldersOpen, setFoldersOpen] = useState(false);
  useEffect(() => { if (!isCompact) setFoldersOpen(false); }, [isCompact]);
  const readingOpen = !!(selectedEmailId || selectedConversationId);
  const [focusMode, setFocusMode] = useState(false);

  // Refs for drag state (avoids stale closure in event listeners)
  const folderDrag = useRef<{ startX: number; startWidth: number } | null>(null);
  const listDrag = useRef<{ startX: number; startWidth: number } | null>(null);

  const matters = useMattersStore((s) => s.matters);
  const storeFetchMatters = useMattersStore((s) => s.fetchMatters);

  // ─── Resizable panels ───────────────────────────────────────

  const startFolderResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    folderDrag.current = { startX: e.clientX, startWidth: folderWidth };

    function onMove(ev: MouseEvent) {
      if (!folderDrag.current) return;
      const next = Math.max(FOLDER_WIDTH_MIN, Math.min(FOLDER_WIDTH_MAX,
        folderDrag.current.startWidth + (ev.clientX - folderDrag.current.startX),
      ));
      setFolderWidth(next);
    }
    function onUp() {
      if (folderDrag.current) {
        try { localStorage.setItem('comm-folder-width', String(folderWidth)); } catch { /* ignore */ }
      }
      folderDrag.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [folderWidth]);

  const startListResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    listDrag.current = { startX: e.clientX, startWidth: listWidth };

    function onMove(ev: MouseEvent) {
      if (!listDrag.current) return;
      const next = Math.max(LIST_WIDTH_MIN, Math.min(LIST_WIDTH_MAX,
        listDrag.current.startWidth + (ev.clientX - listDrag.current.startX),
      ));
      setListWidth(next);
    }
    function onUp() {
      if (listDrag.current) {
        try { localStorage.setItem('comm-list-width', String(listWidth)); } catch { /* ignore */ }
      }
      listDrag.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [listWidth]);


  // ─── Queries ─────────────────────────────────────────────────

  const { data: mailboxes, isLoading: mailboxesLoading } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: fetchMailboxes,
    refetchInterval: 60_000,
  });

  const { data: folderCounts } = useQuery({
    queryKey: ['folder-counts'],
    queryFn: fetchFolderCounts,
    refetchInterval: 30_000,
    enabled: (mailboxes?.length ?? 0) > 0,
  });

  const { data: emailList, isLoading: emailsLoading } = useQuery({
    queryKey: ['inbox-emails', activeFolder, activeMailboxId],
    queryFn: () => fetchEmails({ folder: activeFolder, limit: 50, mailboxId: activeMailboxId ?? undefined }),
    enabled: (mailboxes?.length ?? 0) > 0,
    refetchInterval: 60_000,
  });

  const { data: conversationList, isLoading: conversationsLoading } = useQuery({
    queryKey: ['inbox-conversations', activeFolder, activeMailboxId],
    queryFn: () => fetchConversations({ folder: activeFolder, limit: 50, mailboxId: activeMailboxId ?? undefined }),
    enabled: (mailboxes?.length ?? 0) > 0,
    refetchInterval: 60_000,
  });

  const { data: syncJobs = [] } = useQuery<SyncJob[]>({
    queryKey: ['inbox-sync-status'],
    queryFn: fetchSyncStatus,
    enabled: (mailboxes?.length ?? 0) > 0,
    refetchInterval: 10_000,
  });
  const activeBackfill = syncJobs.filter((j) => j.status === 'running' || j.status === 'pending');

  // The selected conversation summary — feeds the always-on intelligence panel.
  const selectedConversation = selectedConversationId
    ? conversationList?.conversations.find((x) => x.id === selectedConversationId)
    : undefined;

  // Intelligence panel: 'auto' opens it only when the selection has real intel
  // (a linked/suggested matter); the user can pin it open/closed (remembered).
  const [intelPref, setIntelPref] = useState<'auto' | 'open' | 'closed'>(() => {
    try { return (localStorage.getItem('inbox.intelPref') as 'auto' | 'open' | 'closed') || 'auto'; }
    catch { return 'auto'; }
  });
  const hasIntel = !!(
    selectedConversation?.matter ||
    (selectedConversation && selectedConversation.linkStatus === 'suggested') ||
    selectedEmailMatter
  );
  const intelOpen = intelPref === 'open' ? true : intelPref === 'closed' ? false : hasIntel;
  function toggleIntel() {
    const next = intelOpen ? 'closed' : 'open';
    setIntelPref(next);
    try { localStorage.setItem('inbox.intelPref', next); } catch { /* ignore */ }
  }

  const useConversations = !['drafts', 'sent', 'scheduled'].includes(activeFolder);
  const listLoading = useConversations ? conversationsLoading : emailsLoading;
  const listEmpty = useConversations
    ? (conversationList?.conversations.length ?? 0) === 0
    : (emailList?.emails.length ?? 0) === 0;
  const listTotal = useConversations ? (conversationList?.total ?? 0) : (emailList?.total ?? 0);

  // ─── Live inline search — types in the sidebar, results in the same list ──
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAfter, setSearchAfter] = useState('');   // date-wise: from (YYYY-MM-DD)
  const [searchBefore, setSearchBefore] = useState(''); // date-wise: to
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 220);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const hasSearchText = debouncedSearch.length >= 2;
  const isSearching = hasSearchText || !!searchAfter || !!searchBefore;
  const clearSearch = () => { setSearchTerm(''); setSearchAfter(''); setSearchBefore(''); };

  const { data: searchData, isFetching: searchFetching } = useQuery({
    queryKey: ['inbox-livesearch', debouncedSearch, searchAfter, searchBefore, activeMailboxId],
    queryFn: () => searchEmails({
      q: hasSearchText ? debouncedSearch : undefined,
      after: searchAfter || undefined,
      before: searchBefore || undefined,
      mailboxId: activeMailboxId ?? undefined,
      limit: 50,
    }),
    enabled: isSearching,
    staleTime: 10_000,
  });

  // Search results render through the same row component as the folder list.
  const searchResultsAsEmails: EmailSummary[] = (searchData?.results ?? []).map((r: EmailSearchResult) => ({
    id: r.id, mailboxId: r.mailboxId, direction: r.direction as 'inbound' | 'outbound',
    fromAddress: r.fromAddress, toAddresses: r.toAddresses, subject: r.subject, snippet: r.snippet,
    isRead: r.isRead, isStarred: r.isStarred,
    isArchived: false, isTrashed: false, isDraft: false, isSpam: false,
    hasReplyFlag: false, labels: [], providerThreadId: null,
    linkStatus: r.linkStatus as EmailSummary['linkStatus'], linkConfidence: null,
    matterId: r.matterId, matter: r.matter,
    receivedAt: r.receivedAt, sentAt: r.sentAt,
    attachmentCount: r.hasAttachment ? 1 : 0, threadCount: 1, syncStatus: null,
  }));

  const { data: labels = [] } = useQuery({
    queryKey: ['inbox-labels'],
    queryFn: fetchLabels,
    enabled: (mailboxes?.length ?? 0) > 0,
  });

  const { data: mailboxHealth = [] } = useQuery<MailboxHealth[]>({
    queryKey: ['mailbox-health'],
    queryFn: fetchMailboxHealth,
    refetchInterval: 60_000,
    enabled: (mailboxes?.length ?? 0) > 0,
  });

  const degradedMailboxes = mailboxHealth.filter(
    (h) => h.healthStatus === 'error' || h.healthStatus === 'disconnected' || h.healthStatus === 'stale',
  );
  const [healthDetailOpen, setHealthDetailOpen] = useState(false);

  // ─── Single-email mutations ────────────────────────────────

  const archiveMutation = useMutation({
    mutationFn: (emailId: string) => archiveEmail(emailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
      setSelectedEmailId(null);
    },
  });

  const followUpMutation = useMutation({
    mutationFn: (emailId: string) => toggleFollowUp(emailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-email', selectedEmailId] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: ({ emailId, isRead }: { emailId: string; isRead: boolean }) =>
      markRead(emailId, isRead),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails', activeFolder] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
  });

  const starMutation = useMutation({
    mutationFn: (emailId: string) => toggleStar(emailId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails', activeFolder] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-email', selectedEmailId] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
  });

  const conversationStarMutation = useMutation({
    mutationFn: (conversationId: string) => toggleConversationStar(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversation', selectedConversationId] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: () => toast.error('Failed to star conversation'),
  });

  const dismissSuggestionMutation = useMutation({
    mutationFn: (conversationId: string) => dismissConversationSuggestion(conversationId),
    onSuccess: () => {
      toast.success('Suggestion dismissed');
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversation', selectedConversationId] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: () => toast.error('Failed to dismiss suggestion'),
  });

  const linkMatterMutation = useMutation({
    mutationFn: ({ id, matterId, isConversation }: { id: string; matterId: string; isConversation: boolean }) =>
      isConversation ? linkConversationToMatter(id, matterId) : linkEmailToMatter(id, matterId),
    onSuccess: () => {
      toast.success('Linked to matter');
      void queryClient.invalidateQueries({ queryKey: ['inbox-email', matterLinkEmailId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversation', matterLinkEmailId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
      // COMM-1: the Communication Context panel keys on ['comm-context', conversationId] — it must
      // refetch so it reflects the newly-linked matter immediately (was stale for up to 30s).
      void queryClient.invalidateQueries({ queryKey: ['comm-context'] });
      setMatterLinkEmailId(null);
      setMatterLinkIsConversation(false);
      setMatterLinkSearch('');
      setMatterLinkSelected('');
    },
    onError: () => toast.error('Failed to link to matter'),
  });

  // ─── Detach an email from its matter ─────────────────────────
  // The single caller of DELETE /inbox/emails/:id/link. The endpoint and the
  // `unlinkEmailFromMatter` wrapper both already existed and had no caller at all, so a
  // barrister could tag an email to the wrong matter and had no way to take it back.
  // Deliberately mirrors linkMatterMutation — same wrapper style, same invalidation set —
  // because linking and unlinking change exactly the same fact on the same surfaces.
  const unlinkMatterMutation = useMutation({
    mutationFn: (emailId: string) => unlinkEmailFromMatter(emailId),
    onSuccess: (_d, emailId) => {
      toast.success('Unlinked from matter');
      void queryClient.invalidateQueries({ queryKey: ['inbox-email', emailId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
      // Same reason linkMatterMutation invalidates it: the Communication Context panel keys on
      // ['comm-context', conversationId] and would otherwise keep showing the detached matter.
      void queryClient.invalidateQueries({ queryKey: ['comm-context'] });
      // The intelligence panel's fallback anchor is local state, not a query — clear it or the
      // side panel keeps rendering a matter this email is no longer on.
      setSelectedEmailMatter(null);
    },
    onError: (err: unknown) => {
      // getApiErrorMessage, not err.message: the interceptor rejects the raw Axios error, whose
      // `.message` is "Request failed with status code 404". The API's own sentence is the one
      // that tells the barrister what actually happened.
      toast.error(getApiErrorMessage(err, 'Failed to unlink from matter'));
    },
  });

  // ─── Bulk mutations ──────────────────────────────────────────

  const bulkArchiveMutation = useMutation({
    mutationFn: () => bulkArchive([...selectedIds]),
    onSuccess: () => {
      if (selectedIds.has(selectedEmailId ?? '')) setSelectedEmailId(null);
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: () => toast.error('Failed to archive emails'),
  });

  const bulkMarkReadMutation = useMutation({
    mutationFn: (isRead: boolean) => bulkMarkRead([...selectedIds], isRead),
    onSuccess: () => {
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: () => toast.error('Failed to mark emails'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () => bulkDelete([...selectedIds]),
    onSuccess: () => {
      if (selectedIds.has(selectedEmailId ?? '')) setSelectedEmailId(null);
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    },
    onError: () => toast.error('Failed to delete emails'),
  });

  // ─── Selection helpers ───────────────────────────────────────

  function handleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleClearSelection() { setSelectedIds(new Set()); }

  function handleFolderChange(folder: InboxFolder) {
    setActiveFolder(folder);
    setSelectedEmailId(null);
    setSelectedConversationId(null);
    setSelectedIds(new Set());
  }

  async function handleConnect(provider: 'google' | 'microsoft') {
    try {
      const url = await beginOAuthConnect(provider, { returnTo: 'inbox' });
      window.location.href = url;
    } catch {
      toast.error('Could not start connection — please try again');
    }
  }

  // A reconnect names the mailbox, so the server refuses a different account (4C-3). The banner used
  // to start a brand-new connection instead.
  async function handleReconnect(h: MailboxHealth) {
    const provider = oauthProviderForMailbox(h.provider);
    if (!provider) return;
    try {
      const url = await beginOAuthConnect(provider, { mailboxId: h.mailboxId, returnTo: 'inbox' });
      window.location.href = url;
    } catch {
      toast.error('Could not start reconnection — please try again');
    }
  }

  useEffect(() => {
    if (matterLinkEmailId) void storeFetchMatters();
  }, [matterLinkEmailId, storeFetchMatters]);

  useEffect(() => {
    const unsub = onInboxUpdate(() => {
      void queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
    });
    return unsub;
  }, [queryClient]);

  // ─── Keyboard handler callbacks ─────────────────────────────

  const handleCompose = useCallback(() => setComposeOpen(true), []);
  const handleReply = useCallback(() => { /* InboxConversationPane handles its own reply */ }, []);
  const handleReplyAll = useCallback(() => { /* InboxConversationPane handles reply-all */ }, []);
  const handleForward = useCallback(() => { /* InboxConversationPane handles forward */ }, []);
  const handleArchive = useCallback(() => {
    if (selectedEmailId) archiveMutation.mutate(selectedEmailId);
  }, [selectedEmailId, archiveMutation]);
  const handleNext = useCallback(() => {
    if (!useConversations) return;
    const convs = conversationList?.conversations ?? [];
    const idx = convs.findIndex((c) => c.id === selectedConversationId);
    if (idx < convs.length - 1) {
      const next = convs[idx + 1]!;
      setSelectedConversationId(next.id);
      setSelectedEmailMatter(next.matter ?? null);
    }
  }, [useConversations, conversationList, selectedConversationId]);
  const handlePrev = useCallback(() => {
    if (!useConversations) return;
    const convs = conversationList?.conversations ?? [];
    const idx = convs.findIndex((c) => c.id === selectedConversationId);
    if (idx > 0) {
      const prev = convs[idx - 1]!;
      setSelectedConversationId(prev.id);
      setSelectedEmailMatter(prev.matter ?? null);
    }
  }, [useConversations, conversationList, selectedConversationId]);
  const handleBackToList = useCallback(() => {
    setSelectedConversationId(null);
    setSelectedEmailId(null);
  }, []);
  const handleSearch = useCallback(() => navigate('/barrister/inbox/search'), [navigate]);
  const handleToggleFocus = useCallback(() => setFocusMode((f) => !f), []);
  const handleEscape = useCallback(() => {
    if (focusMode) { setFocusMode(false); return; }
    if (selectedConversationId || selectedEmailId) { handleBackToList(); }
  }, [focusMode, selectedConversationId, selectedEmailId, handleBackToList]);

  // ─── Derived ─────────────────────────────────────────────────

  const hasMailboxes = (mailboxes?.length ?? 0) > 0;
  const hasMultipleMailboxes = (mailboxes?.length ?? 0) > 1;
  const activeMailbox = activeMailboxId
    ? (mailboxes?.find((m) => m.id === activeMailboxId) ?? mailboxes?.[0])
    : mailboxes?.[0];
  const anySelected = selectedIds.size > 0;

  if (mailboxesLoading) {
    return (
      <>
        {!standalone && <CommunicationsTabBar />}
        <div style={{ display: 'flex', height: standalone ? '100vh' : 'calc(100vh - 100px)', alignItems: 'center', justifyContent: 'center' }}>
          {standalone && (
            <div className="flex w-[52px] shrink-0 self-stretch flex-col items-center border-r border-slate-800 bg-slate-950 py-3">
              <button onClick={() => navigate('/barrister')} className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-white" title="Back to Jurivoxa">
                <Scales size={17} weight="fill" />
              </button>
            </div>
          )}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: 'var(--text-subtle)' }}>Loading…</div>
          </div>
        </div>
      </>
    );
  }

  // Effective widths in focus mode
  const effectiveFolderWidth = focusMode ? 0 : folderWidth;
  const effectiveListWidth = focusMode ? 0 : listWidth;

  // Single workspace-sidebar folder item (used across the grouped sections).
  const renderFolderItem = (folder: FolderDef) => {
    const displayCount = folderCounts ? getFolderDisplayCount(folder, folderCounts) : null;
    const isActive = activeFolder === folder.id;
    const isSpamFolder = folder.id === 'spam';
    return (
      <div
        key={folder.id}
        onClick={() => handleFolderChange(folder.id)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 16px', cursor: 'pointer',
          background: isActive ? '#1f2937' : 'transparent',
          borderLeft: isActive ? '2px solid #2563EB' : '2px solid transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <folder.Icon size={15} color={isActive ? '#93c5fd' : isSpamFolder ? '#f59e0b' : '#6b7280'} />
          <span style={{
            fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
            color: isActive ? '#f3f4f6' : '#9ca3af',
            fontWeight: isActive ? 500 : 400, whiteSpace: 'nowrap',
          }}>
            {folder.label}
          </span>
        </div>
        {displayCount !== null && displayCount > 0 && (
          <span style={{
            fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11,
            color: isActive ? '#93c5fd' : isSpamFolder ? '#f59e0b' : '#6b7280',
            background: isActive ? 'rgba(37,99,235,0.3)' : '#1f2937',
            padding: '1px 6px', borderRadius: 10,
          }}>
            {displayCount > 999 ? '999+' : displayCount}
          </span>
        )}
      </div>
    );
  };

  const sectionHeader = (label: string) => (
    <div style={{ padding: '12px 16px 4px' }}>
      <span style={{
        fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, fontWeight: 600,
        /*
          FIXED, not tokenised — on purpose. This folder panel is deliberately dark in BOTH themes
          (its container sets a hardcoded dark background and colorScheme: 'dark'), so a
          theme-aware token is the wrong tool here: `var(--text-secondary)` resolves to dark grey
          in LIGHT mode, which put these section labels at 1.74:1 on the dark panel — measured in
          the browser, effectively invisible. A light slate reads at ~7.2:1 against the panel in
          both themes, which is what a fixed surface needs.
        */
        color: '#94a3b8', letterSpacing: '0.09em', textTransform: 'uppercase',
      }}>
        {label}
      </span>
    </div>
  );

  return (
    <>
      {/* Every page needs one top-level heading: it is what a screen reader announces on
          arrival and what heading navigation jumps between. This screen's design has no
          single visible title, so the heading is for assistive technology only and the
          layout is untouched. */}
      <h1 className="sr-only">Inbox</h1>
    {!standalone && <CommunicationsTabBar />}
    <div style={{ display: 'flex', flexDirection: 'column', height: standalone ? '100vh' : 'calc(100vh - 100px)' }}>
    <div style={{ display: 'flex', flex: 1, minHeight: 0, background: 'var(--surface-card)', overflow: 'hidden' }}>

      {/* ── Keyboard handler ── */}
      <InboxKeyboardHandler
        onCompose={handleCompose}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        onArchive={handleArchive}
        onNext={handleNext}
        onPrev={handlePrev}
        onBackToList={handleBackToList}
        onSearch={handleSearch}
        onToggleFocus={handleToggleFocus}
        onEscape={handleEscape}
        enabled={!composeOpen && !matterLinkEmailId && !showConnectModal}
      />

      {/* ── Zone 1: Icon rail — standalone workspace navigation ── */}
      {standalone && !focusMode && !isPhone && (
        <div className="flex w-[52px] shrink-0 flex-col items-center border-r border-slate-800 bg-slate-950 py-3" style={{ zIndex: 10 }}>
          <button
            onClick={() => navigate('/barrister')}
            className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-white transition hover:bg-brand-700"
            title="Back to Jurivoxa"
          >
            <Scales size={17} weight="fill" />
          </button>
          {([
            { Icon: HouseSimple,      label: 'Today',    to: '/barrister' },
            { Icon: CalendarBlank,    label: 'Diary',    to: '/barrister/diary' },
            { Icon: Briefcase,        label: 'Matters',  to: '/barrister/matters' },
            { Icon: ListChecks,       label: 'Tasks',    to: '/barrister/tasks' },
            { Icon: ChatTeardropDots, label: 'Messages', to: '/barrister/communications' },
          ] as const).map(({ Icon, label, to }) => (
            <button
              key={to}
              onClick={() => navigate(to)}
              className="mb-0.5 flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 transition hover:bg-slate-800 hover:text-white"
              title={label}
            >
              <Icon size={18} />
            </button>
          ))}
          <button
            className="mb-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-950 text-blue-400"
            title="Communications (current)"
            aria-current="page"
          >
            <Tray size={18} />
          </button>
          {/* Last in the rail: the folder drawer handle, needed only while the sidebar is
              folded away (below xl). */}
          {isCompact && (
            <button
              onClick={() => setFoldersOpen(true)}
              className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 transition hover:bg-slate-800 hover:text-white"
              title="Folders"
              aria-label="Folders"
            >
              <List size={18} />
            </button>
          )}
        </div>
      )}

      {/* OAuth result — kept on screen (it used to be wiped on first render) and in plain English. */}
      <OAuthReturnNotice floating connectedText="First sync starting…" />
      {degradedMailboxes.length > 0 && (() => {
        const primary = degradedMailboxes[0]!;
        // Action needed (auth/frozen) → red + Reconnect. Self-healing (refreshing /
        // delayed) → calm blue, no button: the system recovers on its own.
        const actionNeeded = degradedMailboxes.some((h) => h.needsUserAction);
        const many = degradedMailboxes.length > 1;
        const bg = actionNeeded ? '#7f1d1d' : '#0f2e4d';
        const accent = actionNeeded ? '#f87171' : '#60a5fa';
        const showDetail = healthDetailOpen;
        const setDetailFn = setHealthDetailOpen;
        return (
          <div style={{
            position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
            background: bg, color: '#f8fafc', padding: '10px 16px', borderRadius: 8,
            fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, zIndex: 100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)', maxWidth: 560,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <WarningCircle size={16} weight="fill" color={accent} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>
                  {many ? `${degradedMailboxes.length} mailboxes need attention` : `${primary.emailAddress} — ${primary.userTitle}`}
                </div>
                {!many && (
                  <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 1 }}>{primary.userDetail}</div>
                )}
              </div>
              {actionNeeded && (
                <button
                  onClick={() => handleReconnect(primary)}
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, fontWeight: 600,
                    color: '#f8fafc', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 4, padding: '4px 10px', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  Reconnect
                </button>
              )}
              {primary.lastSyncError && (
                <button
                  onClick={() => setDetailFn(!showDetail)}
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11,
                    color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  {showDetail ? 'Hide details' : 'Details'}
                </button>
              )}
            </div>
            {showDetail && primary.lastSyncError && (
              <pre style={{
                margin: '8px 0 0', padding: '8px 10px', background: 'rgba(0,0,0,0.35)', borderRadius: 4,
                fontSize: 11, color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', maxHeight: 120, overflow: 'auto',
              }}>
                {primary.lastSyncError}
              </pre>
            )}
          </div>
        );
      })()}

      {/* ── Zone 2: Folder sidebar. Below xl it is a drawer, not a column ── */}
      {isCompact && foldersOpen && (
        <div
          onClick={() => setFoldersOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.55)', zIndex: 40 }}
        />
      )}
      {!focusMode && (!isCompact || foldersOpen) && (
        <div style={{
          width: isCompact ? 240 : effectiveFolderWidth, flexShrink: 0, background: '#111827',
          display: 'flex', flexDirection: 'column', borderRight: '1px solid #1f2937',
          overflow: 'hidden', transition: 'width 0.15s ease',
          ...(isCompact ? { position: 'fixed' as const, top: 0, left: 0, bottom: 0, zIndex: 41 } : {}),
        }}>
          {/* Mailbox header */}
          <div style={{ padding: '20px 16px 12px', flexShrink: 0 }}>
            {activeMailbox ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: providerColor(activeMailbox.provider),
                  }} />
                  <span style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                    color: 'var(--text-subtle)', letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>
                    {providerLabel(activeMailbox.provider)}
                  </span>
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
                  fontWeight: 500, color: '#f3f4f6',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {activeMailbox.emailAddress}
                </div>
                {activeMailbox.status !== 'active' && (
                  <div style={{
                    marginTop: 4, fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: 11, color: '#f59e0b',
                  }}>
                    ⚠ {activeMailbox.status}
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowConnectModal(true)}
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
                  // #2563EB (blue-600) is a LIGHT-background blue, and this sidebar is dark navy
                  // in BOTH themes — it measured 3.43:1 against it, under the 4.5:1 AA floor for
                  // 13px text. blue-400 is the same hue tuned for a dark surface (~7:1). Measured
                  // in the browser, not eyeballed.
                  color: '#60A5FA', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                + Connect mailbox
              </button>
            )}
          </div>

          {/* Search bar — live search as you type (results appear in the list) */}
          {hasMailboxes && (
            <div style={{ padding: '0 12px 10px', flexShrink: 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#1f2937', borderRadius: 6, padding: '7px 10px',
              }}>
                <MagnifyingGlass size={13} color="#6b7280" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search mail…"
                  style={{
                    flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
                    color: '#e5e7eb', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                  }}
                />
                {isSearching && (
                  <button
                    onClick={clearSearch}
                    title="Clear search"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, fontSize: 15, lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>
              {/* Date-wise filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <input
                  type="date"
                  value={searchAfter}
                  onChange={(e) => setSearchAfter(e.target.value)}
                  title="From date"
                  style={{ flex: 1, minWidth: 0, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 5, color: '#cbd5e1', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, padding: '4px 6px', colorScheme: 'dark' }}
                />
                <span style={{ color: '#6b7280', fontSize: 11 }}>–</span>
                <input
                  type="date"
                  value={searchBefore}
                  onChange={(e) => setSearchBefore(e.target.value)}
                  title="To date"
                  style={{ flex: 1, minWidth: 0, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 5, color: '#cbd5e1', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, padding: '4px 6px', colorScheme: 'dark' }}
                />
              </div>
            </div>
          )}

          {/* Compose button */}
          {hasMailboxes && activeMailbox && (
            <div style={{ padding: '0 12px 8px', flexShrink: 0 }}>
              <button
                onClick={() => setComposeOpen(true)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', background: '#2563EB', border: 'none',
                  borderRadius: 6, cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 13, fontWeight: 500, color: 'white',
                }}
              >
                <PencilSimple size={14} />
                Compose
              </button>
            </div>
          )}

          {/* Workflow-grouped folder list (matter-centric, not mailbox-centric) */}
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
            {FOLDER_SECTIONS.map((section) => {
              const visibleIds = section.ids.filter((id) => !HIDDEN_FOLDERS.has(id));
              if (visibleIds.length === 0) return null; // skip a section with nothing to show
              return (
                <div key={section.label}>
                  {sectionHeader(section.label)}
                  {visibleIds.map((id) => {
                    const f = FOLDER_BY_ID[id];
                    return f ? renderFolderItem(f) : null;
                  })}
                </div>
              );
            })}

            {/* "More" — demoted views (spam, trash, etc.), collapsed by default */}
            <div
              onClick={() => setMoreOpen((o) => !o)}
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '12px 16px 4px' }}
            >
              <span style={{
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, fontWeight: 600,
                // Same fixed-dark panel as sectionHeader above — a theme token renders this
                // invisible in light mode (measured 1.74:1). See the note there.
                color: '#94a3b8', letterSpacing: '0.09em', textTransform: 'uppercase',
              }}>
                {moreOpen ? '▾' : '▸'} More
              </span>
            </div>
            {moreOpen && MORE_FOLDER_IDS.filter((id) => !HIDDEN_FOLDERS.has(id)).map((id) => {
              const f = FOLDER_BY_ID[id];
              return f ? renderFolderItem(f) : null;
            })}
          </div>

          {/* Labels section */}
          {hasMailboxes && labels.length > 0 && (
            <>
              <div style={{ margin: '8px 16px 4px', height: 1, background: '#1f2937' }} />
              <div style={{ padding: '6px 16px 2px', flexShrink: 0 }}>
                <span style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10,
                  color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>Labels</span>
              </div>
              {labels.slice(0, 8).map((label) => (
                <div
                  key={label.id}
                  onClick={() => navigate(`/barrister/inbox/search?labelId=${label.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 16px', cursor: 'pointer',
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: label.colour, flexShrink: 0,
                  }} />
                  <span style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                    color: 'var(--text-subtle)', flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {label.name}
                  </span>
                  {label.emailCount > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label.emailCount}</span>
                  )}
                </div>
              ))}
              <div
                onClick={() => navigate('/barrister/inbox/settings/labels')}
                style={{
                  padding: '4px 16px', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11,
                  color: 'var(--text-secondary)',
                }}
              >
                Manage labels…
              </div>
            </>
          )}
          {hasMailboxes && labels.length === 0 && (
            <>
              <div style={{ margin: '8px 16px', height: 1, background: '#1f2937' }} />
              <div
                onClick={() => navigate('/barrister/inbox/settings/labels')}
                style={{
                  padding: '6px 16px', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11,
                  color: 'var(--text-secondary)',
                }}
              >
                + Create label
              </div>
            </>
          )}

          {/* Add mailbox */}
          {hasMailboxes && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #1f2937', flexShrink: 0 }}>
              <button
                onClick={() => setShowConnectModal(true)}
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                  color: 'var(--text-muted)', background: 'none', border: '1px solid #374151',
                  borderRadius: 6, padding: '6px 10px', cursor: 'pointer', width: '100%',
                }}
              >
                + Add mailbox
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Drag handle: Zone 2 / Zone 3 ── */}
      {!focusMode && !isCompact && (
        <div
          onMouseDown={startFolderResize}
          style={{
            width: 4, flexShrink: 0, cursor: 'col-resize',
            background: 'transparent',
            borderRight: '1px solid var(--border-subtle)',
          }}
          title="Drag to resize folder sidebar"
        />
      )}

      {/* ── Main content ── */}
      {!hasMailboxes ? (
        <ConnectMailboxPanel onConnect={handleConnect} />
      ) : (
        <>
          {/* ── Zone 3: Conversation list pane ── */}
          {!focusMode && !(isPhone && readingOpen) && (
            <div style={{
              width: isPhone ? '100%' : effectiveListWidth,
              flexShrink: isPhone ? 1 : 0, minWidth: 0,
              borderRight: '1px solid var(--border-default)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              transition: 'width 0.15s ease',
            }}>
              {/* List header */}
              <div style={{
                padding: '12px 16px', borderBottom: '1px solid var(--border-default)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Below xl the folder sidebar is a drawer; this is the only way to open it. */}
                  {isPhone && (
                    <button
                      onClick={() => setFoldersOpen(true)}
                      title="Folders"
                      aria-label="Folders"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        height: 28, width: 28, flexShrink: 0, borderRadius: 8,
                        color: 'var(--text-subtle)', background: 'transparent', border: 'none', cursor: 'pointer',
                      }}
                    >
                      <List size={16} />
                    </button>
                  )}
                  <span style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 15,
                    fontWeight: 600, color: 'var(--text-primary)',
                  }}>
                    {FOLDERS.find((f) => f.id === activeFolder)?.label}
                  </span>
                  {listTotal > 0 && (
                    <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)' }}>
                      {listTotal}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!standalone && (
                    <button
                      onClick={() => window.open('/barrister/inbox/full', '_blank')}
                      title="Open in full workspace"
                      style={{
                        padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border-default)',
                        background: 'var(--surface-card)', cursor: 'pointer', color: 'var(--text-muted)',
                        lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center',
                      }}
                    >
                      <ArrowSquareOut size={15} />
                    </button>
                  )}
                </div>
              </div>

              {/* Multi-account switcher */}
              {hasMultipleMailboxes && (
                <div style={{
                  display: 'flex', gap: 0, flexShrink: 0,
                  borderBottom: '1px solid var(--border-default)', padding: '0 12px',
                  overflowX: 'auto',
                }}>
                  <button
                    onClick={() => { setActiveMailboxId(null); setSelectedEmailId(null); }}
                    style={{
                      padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer',
                      fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                      color: activeMailboxId === null ? '#111827' : '#9ca3af',
                      fontWeight: activeMailboxId === null ? 600 : 400,
                      borderBottom: activeMailboxId === null ? '2px solid #2563EB' : '2px solid transparent',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    All
                  </button>
                  {(mailboxes ?? []).map((mb) => (
                    <button
                      key={mb.id}
                      onClick={() => { setActiveMailboxId(mb.id); setSelectedEmailId(null); }}
                      style={{
                        padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer',
                        fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
                        color: activeMailboxId === mb.id ? '#111827' : '#9ca3af',
                        fontWeight: activeMailboxId === mb.id ? 600 : 400,
                        borderBottom: activeMailboxId === mb.id ? `2px solid ${providerColor(mb.provider)}` : '2px solid transparent',
                        whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: providerColor(mb.provider), display: 'inline-block', flexShrink: 0 }} />
                      {mb.emailAddress.split('@')[0]}
                    </button>
                  ))}
                </div>
              )}

              {/* Bulk bar */}
              {anySelected && (
                <InboxBulkBar
                  count={selectedIds.size}
                  onArchive={() => bulkArchiveMutation.mutate()}
                  onMarkRead={() => bulkMarkReadMutation.mutate(true)}
                  onMarkUnread={() => bulkMarkReadMutation.mutate(false)}
                  onDelete={() => bulkDeleteMutation.mutate()}
                  onClear={handleClearSelection}
                />
              )}

              {/* Backfill progress banner */}
              {activeBackfill.length > 0 && (
                <div style={{
                  padding: '7px 14px', background: '#0f1f3d', flexShrink: 0,
                  borderBottom: '1px solid #1e3a5f', display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#2563EB',
                    animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0,
                  }} />
                  <span style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: '#93c5fd', flex: 1,
                  }}>
                    {activeBackfill[0]!.totalEstimated
                      ? `Importing: ${activeBackfill[0]!.totalProcessed.toLocaleString()} / ~${activeBackfill[0]!.totalEstimated.toLocaleString()}`
                      : `Importing… ${activeBackfill[0]!.totalProcessed.toLocaleString()}`}
                  </span>
                  {activeBackfill[0]!.totalEstimated && activeBackfill[0]!.totalProcessed > 0 && (
                    <div style={{ width: 60, height: 4, background: '#1e3a5f', borderRadius: 2, flexShrink: 0, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 2, background: '#2563EB',
                        width: `${Math.min(100, (activeBackfill[0]!.totalProcessed / activeBackfill[0]!.totalEstimated!) * 100)}%`,
                      }} />
                    </div>
                  )}
                </div>
              )}

              {/* Email / Conversation rows */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                {isSearching ? (
                  searchFetching && !searchData ? (
                    <div style={{ padding: 24, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-subtle)' }}>
                      Searching…
                    </div>
                  ) : searchResultsAsEmails.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-muted)' }}>
                      {hasSearchText ? `No results for “${debouncedSearch}”` : 'No emails in that date range'}
                    </div>
                  ) : (
                    searchResultsAsEmails.map((email) => (
                      <InboxEmailRow
                        key={email.id}
                        email={email}
                        isSelected={selectedEmailId === email.id}
                        anySelected={anySelected}
                        onSelect={handleSelect}
                        onStarToggle={(id) => starMutation.mutate(id)}
                        onArchive={(id) => archiveMutation.mutate(id)}
                        onMarkRead={(id, isRead) => markReadMutation.mutate({ emailId: id, isRead })}
                        onDelete={(id) => { setSelectedIds(new Set([id])); bulkDeleteMutation.mutate(); }}
                        onClick={(id) => {
                          setSelectedEmailId(id);
                          setSelectedConversationId(null);
                          setSelectedIds(new Set());
                          const e = searchResultsAsEmails.find((em) => em.id === id);
                          setSelectedEmailMatter(e?.matter ?? null);
                        }}
                      />
                    ))
                  )
                ) : listLoading ? (
                  <div style={{ padding: 24, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-subtle)' }}>
                    Loading…
                  </div>
                ) : listEmpty ? (
                  <div style={{ padding: 32, textAlign: 'center' }}>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                      {(() => { const FolderIcon = FOLDERS.find(f => f.id === activeFolder)?.Icon; return FolderIcon ? <FolderIcon size={32} color="#d1d5db" /> : null; })()}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-muted)' }}>
                      No mail in {FOLDERS.find((f) => f.id === activeFolder)?.label.toLowerCase()}
                    </div>
                    {activeFolder === 'inbox' && activeBackfill.length === 0 && (
                      <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', marginTop: 8 }}>
                        First sync may still be in progress
                      </div>
                    )}
                  </div>
                ) : useConversations ? (
                  (conversationList?.conversations ?? []).map((conv: ConversationSummary) => (
                    <InboxConversationRow
                      key={conv.id}
                      conversation={conv}
                      isSelected={selectedConversationId === conv.id}
                      anySelected={anySelected}
                      onSelect={handleSelect}
                      onStarToggle={(id) => conversationStarMutation.mutate(id)}
                      onArchive={() => { /* conversation archive — phase 2 */ }}
                      onClick={(id) => {
                        setSelectedConversationId(id);
                        setSelectedEmailId(null);
                        setSelectedIds(new Set());
                        const c = conversationList?.conversations.find((x) => x.id === id);
                        setSelectedEmailMatter(c?.matter ?? null);
                      }}
                    />
                  ))
                ) : (
                  (emailList?.emails ?? []).map((email: EmailSummary) => (
                    <InboxEmailRow
                      key={email.id}
                      email={email}
                      isSelected={selectedEmailId === email.id}
                      anySelected={anySelected}
                      onSelect={handleSelect}
                      onStarToggle={(id) => starMutation.mutate(id)}
                      onArchive={(id) => archiveMutation.mutate(id)}
                      onMarkRead={(id, isRead) => markReadMutation.mutate({ emailId: id, isRead })}
                      onDelete={(id) => {
                        const single = new Set([id]);
                        setSelectedIds(single);
                        bulkDeleteMutation.mutate();
                      }}
                      onClick={(id) => {
                        setSelectedEmailId(id);
                        setSelectedConversationId(null);
                        setSelectedIds(new Set());
                        const e = emailList?.emails.find((em) => em.id === id);
                        setSelectedEmailMatter(e?.matter ?? null);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Drag handle: Zone 3 / Zone 4 ── */}
          {!focusMode && !isPhone && (
            <div
              onMouseDown={startListResize}
              style={{
                width: 4, flexShrink: 0, cursor: 'col-resize',
                background: 'transparent',
                borderRight: '1px solid var(--border-subtle)',
              }}
              title="Drag to resize conversation list"
            />
          )}

          {/* ── Zone 4: Reading + always-on Matter Intelligence ── */}
          <div style={{ flex: 1, overflow: 'hidden', display: isPhone && !readingOpen ? 'none' : 'flex', flexDirection: 'row' }}>
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Focus mode indicator */}
            {focusMode && (
              <div style={{
                position: 'absolute', top: 10, right: 14, zIndex: 30,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11,
                  color: 'var(--text-subtle)', background: 'var(--surface-hover)',
                  padding: '3px 8px', borderRadius: 4,
                }}>
                  Focus mode — press F or Esc to exit
                </span>
              </div>
            )}

            {/* On a phone the list is replaced by the email, so this is the only way back. */}
            {isPhone && readingOpen && (
              <button
                onClick={handleBackToList}
                aria-label="Back to messages"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                  padding: '10px 12px', border: 'none', background: 'transparent',
                  borderBottom: '1px solid var(--border-default)',
                  color: 'var(--text-subtle)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                }}
              >
                <CaretLeft size={14} weight="bold" />Messages
              </button>
            )}

            {selectedConversationId ? (
              <InboxConversationPane
                conversationId={selectedConversationId}
                onFollowUp={() => {
                  void queryClient.invalidateQueries({ queryKey: ['inbox-conversation', selectedConversationId] });
                }}
                onLinkMatter={() => { setMatterLinkEmailId(selectedConversationId); setMatterLinkIsConversation(true); }}
                onAcceptSuggestion={(matterId) => linkMatterMutation.mutate({ id: selectedConversationId, matterId, isConversation: true })}
                onDismissSuggestion={() => dismissSuggestionMutation.mutate(selectedConversationId)}
                onCreateTask={(matterId, matterName) => navigate(`/barrister/tasks?matterId=${matterId}&matterName=${encodeURIComponent(matterName)}&source=email`)}
                onCreateHearingNote={(matterId) => navigate(`/barrister/matters/${matterId}?tab=timeline`)}
                onLogTime={(matterId) => navigate(`/barrister/activities?matterId=${matterId}&source=email`)}
                onGenerateFeeEntry={(matterId) => navigate(`/barrister/fees?matterId=${matterId}&source=email`)}
                onSaveToDocuments={(matterId) => navigate(`/barrister/documents?matterId=${matterId}&source=email`)}
              />
            ) : selectedEmailId ? (
              <InboxThreadPane
                emailId={selectedEmailId}
                onArchive={() => archiveMutation.mutate(selectedEmailId)}
                onFollowUp={() => followUpMutation.mutate(selectedEmailId)}
                onLinkMatter={() => { setMatterLinkEmailId(selectedEmailId); setMatterLinkIsConversation(false); }}
                onUnlinkMatter={() => unlinkMatterMutation.mutate(selectedEmailId)}
                onCreateTask={(matterId, matterName) => navigate(`/barrister/tasks?matterId=${matterId}&matterName=${encodeURIComponent(matterName)}&source=email`)}
                onCreateHearingNote={(matterId) => navigate(`/barrister/matters/${matterId}?tab=timeline`)}
                onLogTime={(matterId) => navigate(`/barrister/activities?matterId=${matterId}&source=email`)}
                onGenerateFeeEntry={(matterId) => navigate(`/barrister/fees?matterId=${matterId}&source=email`)}
                onSaveToDocuments={(matterId) => navigate(`/barrister/documents?matterId=${matterId}&source=email`)}
              />
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                    <EnvelopeOpen size={40} color="#d1d5db" />
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-subtle)' }}>
                    Select a conversation to read
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)', marginTop: 12, opacity: 0.6 }}>
                    Press F for focus mode · j/k to navigate · c to compose · / to search
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Legal Communication Intelligence — collapsible (auto) ── */}
          {!focusMode && !isCompact && (intelOpen ? (
            <div style={{ width: 340, flexShrink: 0, borderLeft: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 10px 7px 14px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-subtle)' }}>
                  <Sparkle size={13} weight="fill" color="#7c3aed" /> Intelligence
                </span>
                <button
                  onClick={toggleIntel}
                  title="Collapse intelligence panel"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, border: 'none', borderRadius: 5, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer', padding: 0 }}
                >
                  <CaretRight size={15} />
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                {selectedConversationId ? (
                  // Conversation selected → the assembled Communication Context (pure renderer).
                  <CommunicationContextPanel
                    conversationId={selectedConversationId}
                    onLinkMatter={() => { setMatterLinkEmailId(selectedConversationId); setMatterLinkIsConversation(true); }}
                    onNavigate={(entity, id) => {
                      if (entity === 'Hearing') navigate(`/barrister/hearings/${id}`);
                      else if (entity === 'Case') navigate(`/barrister/matters/${id}`);
                      else if (entity === 'FeeNote') navigate('/barrister/fees');
                    }}
                  />
                ) : (
                  // Single email selected (search / sent / drafts) — legacy fallback anchor.
                  <ConversationIntelligencePanel
                    conversation={null}
                    fallbackMatter={selectedEmailMatter}
                    onAccept={() => { /* n/a for a single email */ }}
                    onDismiss={() => { /* n/a */ }}
                    onChange={() => { if (selectedEmailId) { setMatterLinkEmailId(selectedEmailId); setMatterLinkIsConversation(false); } }}
                    onCreateTask={(matterId) => navigate(`/barrister/tasks?matterId=${matterId}&source=email`)}
                    onCreateHearingNote={(matterId) => navigate(`/barrister/matters/${matterId}?tab=timeline`)}
                    onLogTime={(matterId) => navigate(`/barrister/activities?matterId=${matterId}&source=email`)}
                    onSaveToDocuments={(matterId) => navigate(`/barrister/documents?matterId=${matterId}&source=email`)}
                    onGenerateFeeEntry={(matterId) => navigate(`/barrister/fees?matterId=${matterId}&source=email`)}
                  />
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={toggleIntel}
              title="Show intelligence panel"
              style={{
                width: 34, flexShrink: 0, borderLeft: '1px solid var(--border-default)', background: 'var(--surface-card)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, paddingTop: 12,
                cursor: 'pointer', color: hasIntel ? '#7c3aed' : 'var(--text-subtle)',
              }}
            >
              <CaretLeft size={15} />
              <Sparkle size={16} weight={hasIntel ? 'fill' : 'regular'} />
              {hasIntel && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#7c3aed' }} />}
            </button>
          ))}
          </div>
        </>
      )}

      {/* Compose modal */}
      {composeOpen && activeMailbox && (
        <ComposeModal
          fromAddress={activeMailbox.emailAddress}
          onClose={() => setComposeOpen(false)}
        />
      )}

      {/* Matter link modal */}
      {matterLinkEmailId && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
          }}
          onClick={() => { setMatterLinkEmailId(null); setMatterLinkIsConversation(false); setMatterLinkSearch(''); setMatterLinkSelected(''); }}
        >
          <div
            style={{
              background: 'var(--surface-card)', borderRadius: 12, padding: 28, width: 460,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 17,
              fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16, marginTop: 0,
            }}>
              Link to matter
            </h3>
            <input
              autoFocus
              value={matterLinkSearch}
              onChange={(e) => setMatterLinkSearch(e.target.value)}
              placeholder="Search matters by name or reference…"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6,
                border: '1px solid var(--border-strong)', fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
              }}
            />
            <div style={{
              maxHeight: 260, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 6,
            }}>
              {matters
                .filter((m) =>
                  !matterLinkSearch ||
                  m.name.toLowerCase().includes(matterLinkSearch.toLowerCase()) ||
                  (m.number ?? '').toLowerCase().includes(matterLinkSearch.toLowerCase()),
                )
                .slice(0, 40)
                .map((m) => (
                  <div
                    key={m.id}
                    onClick={() => setMatterLinkSelected(m.id)}
                    style={{
                      padding: '10px 14px', cursor: 'pointer',
                      background: matterLinkSelected === m.id ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                      borderLeft: matterLinkSelected === m.id ? '3px solid #2563EB' : '3px solid transparent',
                    }}
                  >
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                      {m.name}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {m.number}
                    </div>
                  </div>
                ))}
              {matters.length === 0 && (
                <div style={{
                  padding: '20px', textAlign: 'center',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-subtle)',
                }}>
                  Loading matters…
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  const id = matterLinkEmailId;
                  if (!id) return;
                  const name = matterLinkSearch.trim();
                  // New instruction → create a matter from it; the email/conversation auto-links
                  // on success (NewMatterPage handles the link). The typed search becomes the name.
                  navigate('/barrister/matters/new', {
                    state: {
                      ...(name ? { fromIntake: { name } } : {}),
                      ...(matterLinkIsConversation ? { linkConversationId: id } : { linkEmailId: id }),
                    },
                  });
                }}
                style={{
                  marginRight: 'auto', padding: '7px 16px', borderRadius: 6,
                  border: '1px solid var(--border-strong)', background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#2563EB', fontWeight: 500,
                }}
              >
                + Create new matter
              </button>
              {/* The PRE-MATTER sibling. "Create new matter" assumes the work is already
                  ours; most incoming enquiries never become matters, so this is the honest
                  first step for a conversation that is new work rather than correspondence.
                  Conversation-scoped only — an enquiry is raised from a thread, not a single
                  message, because one instruction routinely arrives across several emails. */}
              {matterLinkIsConversation && (
                <button
                  type="button"
                  onClick={() => {
                    const id = matterLinkEmailId;
                    if (!id) return;
                    setEnquiryConversationId(id);
                    setMatterLinkEmailId(null);
                    setMatterLinkIsConversation(false);
                    setMatterLinkSearch('');
                    setMatterLinkSelected('');
                  }}
                  style={{
                    padding: '7px 16px', borderRadius: 6,
                    border: '1px solid var(--border-strong)', background: 'var(--surface-card)', cursor: 'pointer',
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#2563EB', fontWeight: 500,
                  }}
                >
                  Start an enquiry
                </button>
              )}
              <button
                onClick={() => { setMatterLinkEmailId(null); setMatterLinkIsConversation(false); setMatterLinkSearch(''); setMatterLinkSelected(''); }}
                style={{
                  padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border-strong)',
                  background: 'var(--surface-card)', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-primary)',
                }}
              >
                Cancel
              </button>
              <button
                disabled={!matterLinkSelected || linkMatterMutation.isPending}
                onClick={() => {
                  if (matterLinkSelected) {
                    linkMatterMutation.mutate({ id: matterLinkEmailId!, matterId: matterLinkSelected, isConversation: matterLinkIsConversation });
                  }
                }}
                style={{
                  padding: '7px 16px', borderRadius: 6, border: 'none',
                  background: matterLinkSelected ? '#2563EB' : '#d1d5db',
                  cursor: matterLinkSelected ? 'pointer' : 'not-allowed',
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
                  color: 'white', fontWeight: 500,
                }}
              >
                {linkMatterMutation.isPending ? 'Linking…' : 'Link to matter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start an enquiry from a conversation classified as new work.
          The SAME form as "Log an enquiry" in the Intake Inbox — one enquiry form, one set of
          rules, one validator — seeded with what the email can actually tell us. The lay
          client, which an email never states, is supplied by the person before anything is
          created. Nothing is written until they submit. */}
      <LogEnquiryModal
        open={Boolean(enquiryConversationId)}
        conversationId={enquiryConversationId ?? undefined}
        prefill={
          enquiryConversationId
            ? {
                solicitorEmail: selectedConversation?.lastFromAddress,
                summary: selectedConversation?.subject,
              }
            : undefined
        }
        onClose={() => setEnquiryConversationId(null)}
        onLogged={() => {
          void queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
          void queryClient.invalidateQueries({ queryKey: ['inbox-conversation'] });
        }}
      />

      {/* Add mailbox modal */}
      {showConnectModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
          }}
          onClick={() => setShowConnectModal(false)}
        >
          <div
            style={{
              background: 'var(--surface-card)', borderRadius: 12, padding: 32,
              width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 18,
              fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8,
            }}>
              Add a mailbox
            </h3>
            <p style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14,
              color: 'var(--text-muted)', marginBottom: 24,
            }}>
              Connect another email account to Jurivoxa.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={() => { setShowConnectModal(false); void handleConnect('google'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                  border: '1px solid var(--border-strong)', borderRadius: 8,
                  color: 'var(--text-primary)', fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 14, background: 'var(--surface-card)', cursor: 'pointer',
                }}
              >
                Gmail / Google Workspace
              </button>
              <button
                onClick={() => { setShowConnectModal(false); void handleConnect('microsoft'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                  border: '1px solid var(--border-strong)', borderRadius: 8,
                  color: 'var(--text-primary)', fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 14, background: 'var(--surface-card)', cursor: 'pointer',
                }}
              >
                Microsoft 365
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    {standalone && !focusMode && isPhone && (
      <nav
        aria-label="Inbox navigation"
        style={{
          display: 'flex', alignItems: 'stretch', flexShrink: 0,
          background: '#020617', borderTop: '1px solid #1f2937',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          position: 'relative', zIndex: 50,
        }}
      >
        {/* A labelled bottom bar: seven equal targets, each icon over its name. Bare icons
            bunched at one end read as leftovers of the desktop rail. Today returns to
            Jurivoxa, which is what the logo used to do. */}
        {([
          { Icon: List,             label: 'Folders',  onClick: () => setFoldersOpen(true), current: false },
          { Icon: HouseSimple,      label: 'Today',    onClick: () => navigate('/barrister'), current: false },
          { Icon: CalendarBlank,    label: 'Diary',    onClick: () => navigate('/barrister/diary'), current: false },
          { Icon: Briefcase,        label: 'Matters',  onClick: () => navigate('/barrister/matters'), current: false },
          { Icon: ListChecks,       label: 'Tasks',    onClick: () => navigate('/barrister/tasks'), current: false },
          { Icon: ChatTeardropDots, label: 'Messages', onClick: () => navigate('/barrister/communications'), current: false },
          { Icon: Tray,             label: 'Inbox',    onClick: undefined, current: true },
        ] as { Icon: ElementType; label: string; onClick?: () => void; current: boolean }[]).map(({ Icon, label, onClick, current }) => (
          <button
            key={label}
            onClick={onClick}
            aria-label={label}
            aria-current={current ? 'page' : undefined}
            style={{
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3,
              padding: '7px 2px 8px', border: 'none', background: 'transparent',
              color: current ? '#60a5fa' : '#94a3b8',
              cursor: current ? 'default' : 'pointer',
            }}
          >
            <Icon size={19} weight={current ? 'fill' : 'regular'} />
            <span style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, fontWeight: 500,
              lineHeight: 1, letterSpacing: '0.01em', maxWidth: '100%', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          </button>
        ))}
      </nav>
    )}
    </div>
    </>
  );
}
